import useGeneralConfiguration from "features/configuration/GeneralConfiguration/useGeneralConfiguration";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import React, { useContext } from "react";
import { BotType, OverrideFieldType } from "@shared/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import Select from "common/Select/Select";
import styled from "styled-components";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import useMeasure from "utils/hooks/useMeasure";
import { getConfigurationItem } from "@shared/features/bots/utils";
import { Steps, ToggleField } from "@shared/features/bots/BotForm/common/types";
import useStop from "@shared/features/bots/BotForm/Strategies/useStop";
import { OrderTypes } from "@shared/features/trades/common/types";
import DefaultOrder from "features/bots/BotForm/common/DefaultOrder";
import RestoreChangesButton from "features/bots/BotForm/common/RestoreChangesButton";
import {
  LeverageTrailingText,
  SectionTitle,
} from "features/bots/BotForm/common/styles";
import { LEVERAGED_TRAILING_MESSAGE } from "@shared/features/bots/BotForm/common/constants";
import OverrideToggle from "features/bots/BotForm/common/OverrideToggle";
import { openMessage } from "utils/misc";
import { OverlayWrapper } from "features/bots/BotForm/Strategies/StrategyNumOrders";

const Stop = () => {
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { botType, botGeneralData, isChannelConfiguration } =
    useContext(BotFormContext)!;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const trailingStopItem = getConfigurationItem(
    botGeneralData,
    "Trailing Stop"
  );

  const { generalConfiguration, modifiedConfiguration } =
    useGeneralConfiguration({
      configuration: trailingStopItem,
      showOverrides: false,
      colorModifiedValues: false,
      useCard: false,
      useDefaultPopupContainer: true,
    });

  const {
    stopTimeoutSelectorData,
    numContinuousStopsSelectorData,
    getDefaultTrailingStop,
    trailingStopTitle,
    isLeveraged,
    skipWithoutStopLossData,
  } = useStop(modifiedConfiguration);

  const stopTimeoutSelect = (
    <Select
      style={{ minWidth: 250 }}
      value={stopTimeoutSelectorData.value}
      onChange={stopTimeoutSelectorData.onChange}
      popupMatchSelectWidth={false}
      optionLabelProp="label"
      placeholder={stopTimeoutSelectorData.placeholder}
      options={stopTimeoutSelectorData.options.map((option) => ({
        value: option.value,
        label: option.name,
      }))}
      mobileTitle={stopTimeoutSelectorData.title}
    />
  );

  const getSkipWithoutStop = () => {
    if (!skipWithoutStopLossData.shouldDisplay) return;
    return (
      <div
        style={{
          marginBottom: 16,
          position: "relative",
        }}
      >
        {!!skipWithoutStopLossData.clickWarning && (
          <OverlayWrapper
            onClick={() =>
              openMessage(skipWithoutStopLossData.clickWarning, "warning")
            }
          />
        )}
        <TitleWithToggle
          title={skipWithoutStopLossData.title}
          fieldType={ToggleField.SKIP_WITHOUT_STOP_LOSS}
          style={{ marginTop: 20 }}
        />
      </div>
    );
  };

  const getNumContinuousStops = () => {
    if (!numContinuousStopsSelectorData.shouldDisplay) return;
    return (
      <EqualColumnsGrid
        numColumns={useSingleColumnLayout ? 1 : 2}
        style={{ marginTop: 16 }}
        ref={measureRef}
      >
        <div>
          <FormFieldTitle style={{ whiteSpace: "nowrap", paddingRight: 20 }}>
            {numContinuousStopsSelectorData.title}{" "}
            <HelperTooltip {...numContinuousStopsSelectorData.helperData} />
          </FormFieldTitle>
          <CustomInputNumber
            onChange={numContinuousStopsSelectorData.onChange}
            bordered={numContinuousStopsSelectorData.bordered}
            value={numContinuousStopsSelectorData.value}
            precision={numContinuousStopsSelectorData.precision}
            placeholder={numContinuousStopsSelectorData.placeholder}
            stringMode={true}
            useDebounce={true}
          />
          {numContinuousStopsSelectorData.error && (
            <ErrorText>{numContinuousStopsSelectorData.error}</ErrorText>
          )}
        </div>
      </EqualColumnsGrid>
    );
  };

  const useSingleColumnLayout = width < 380;

  const trailingStop = (
    <ItemWrapper style={isChannelConfiguration ? { marginTop: 0 } : undefined}>
      <TitleWithToggle
        title={trailingStopTitle}
        content={
          <div style={{ marginTop: -12 }}>
            {generalConfiguration}
            {isLeveraged && (
              <LeverageTrailingText>
                * {t(LEVERAGED_TRAILING_MESSAGE)}
              </LeverageTrailingText>
            )}
            <OverrideToggle overrideField={OverrideFieldType.TRAILING_STOP} />
          </div>
        }
        fieldType={ToggleField.TRAILING_STOP}
        getDefaultValue={getDefaultTrailingStop}
      />
    </ItemWrapper>
  );

  const stopTimeout = stopTimeoutSelectorData.shouldDisplay ? (
    <ItemWrapper>
      <TitleWithToggle
        title={stopTimeoutSelectorData.title}
        content={stopTimeoutSelect}
        fieldType={ToggleField.STOP_TIMEOUT}
      />
    </ItemWrapper>
  ) : undefined;

  const content = (
    <ContentWrapper>
      <DefaultOrder orderType={OrderTypes.STOP} />
      {isLeveraged && (
        <LeverageTrailingText>
          * {t(LEVERAGED_TRAILING_MESSAGE)}
        </LeverageTrailingText>
      )}
      {!isSignalsBot && getNumContinuousStops()}
      {!isSignalsBot && stopTimeout}
      {botType !== BotType.ASAP && !isSignalsBot && !isGridBot && trailingStop}
    </ContentWrapper>
  );

  return (
    <div style={{ position: "relative" }}>
      <RestoreChangesButton tab={Steps.STOP} />
      {!isChannelConfiguration && (
        <TitleWithToggle
          title={<SectionTitle>{t("labels.stop")}</SectionTitle>}
          content={
            <>
              {content}
              <OverrideToggle overrideField={OverrideFieldType.DEFAULT_STOP} />
            </>
          }
          fieldType={ToggleField.STOP}
          size="default"
          hideWithOpacity={true}
        />
      )}
      {isSignalsBot && !isChannelConfiguration && (
        <>
          {getNumContinuousStops()}
          {getSkipWithoutStop()}
          {stopTimeout}
        </>
      )}
      {isSignalsBot && trailingStop}
    </div>
  );
};

export default React.memo(Stop);

const ItemWrapper = styled.div`
  margin-top: 16px;
`;
const ContentWrapper = styled.div`
  position: relative;
`;
